import { useEffect, type RefObject } from 'react';

/**
 * Dismiss-on-outside-press for floating surfaces (popovers, dropdowns,
 * autocompletes).
 *
 * Listens on `document` in the capture phase rather than using a full-screen
 * click-catcher element, so dismissal works regardless of z-index stacking
 * (a press on a higher-z surface like the bottom nav still closes) and can't
 * be defeated by `stopPropagation` lower in the tree.
 *
 * Presses inside a portaled overlay (`[data-overlay-root]` — Dialog /
 * BottomSheet backdrops) are ignored unless that overlay contains `ref`,
 * because such an overlay is a layer stacked ABOVE the floating surface —
 * e.g. the notification-detail Dialog deliberately keeps the notifications
 * panel open underneath it.
 *
 * @param ref - Root of the floating surface INCLUDING its trigger; presses
 *   inside it never dismiss (the trigger's own toggle handles them, which
 *   avoids a close-then-reopen flicker).
 * @param onOutside - Called once per outside press.
 * @param enabled - Attach the listener only while the surface is open.
 */
export function useClickOutside(ref: RefObject<HTMLElement | null>, onOutside: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    function handlePointerDown(e: PointerEvent) {
      const root = ref.current;
      if (!root || !(e.target instanceof Element) || root.contains(e.target)) return;
      // A CSS-hidden surface must not react: the TopBar bell popover stays
      // mounted-but-hidden on desktop while the SideNav popover (same store
      // flag) is the visible one — without this check the hidden instance
      // would treat a press inside the visible panel as "outside" and close both.
      if (root.checkVisibility && !root.checkVisibility()) return;
      const overlay = e.target.closest('[data-overlay-root]');
      if (overlay && !overlay.contains(root)) return;
      onOutside();
    }

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [ref, onOutside, enabled]);
}
